/**
 * Product Hooks - Data Transformation Utilities
 *
 * This file consolidates ALL product transformation logic that was previously
 * duplicated across 8+ different places in the codebase.
 *
 * Benefits:
 * - Fix transformation logic once, it works everywhere
 * - Single source of truth for price/image handling
 * - Easier to debug and maintain
 * - Resolves "7 different field names for price" issue
 */

import type { Product, RawProductResponse } from "./types";
import { getLastProductImageUrl } from "../../utils/imageUtils";

// ============================================================================
// MAIN TRANSFORMATION FUNCTION
// ============================================================================

/**
 * Transform raw RPC response into standardized Product interface
 *
 * This function handles all the inconsistencies in the raw data:
 * - Multiple possible field names for price
 * - Different image formats (arrays, objects, paths)
 * - Optional/missing fields with safe defaults
 * - Calculated fields (original_price for sales)
 *
 * @param raw - Raw product data from RPC or database
 * @param options - Additional context for transformation
 * @returns Standardized Product object
 */
export function transformRawProduct(
  raw: RawProductResponse,
  options?: {
    sortType?: string;
    includeCalculations?: boolean;
  },
): Product {
  const { sortType, includeCalculations: _includeCalculations = true } =
    options || {};

  // Derive variant-aggregated fields from variant_tree.skus when the backend
  // didn't provide them (e.g. raw cached product from localStorage).
  const skus: any[] | undefined = (raw as any).variant_tree?.skus;
  const skuPrices = Array.isArray(skus)
    ? skus
        .map((s) => (typeof s.usd_price === "number" ? s.usd_price : undefined))
        .filter((p): p is number => typeof p === "number")
    : [];
  const derivedMinVariantPrice =
    raw.min_variant_price ?? (skuPrices.length ? Math.min(...skuPrices) : undefined);
  const derivedMaxVariantPrice =
    raw.max_variant_price ?? (skuPrices.length ? Math.max(...skuPrices) : undefined);
  const skuInStock = Array.isArray(skus)
    ? skus.some(
        (s) =>
          s.is_unlimited_stock ||
          ((s.quantity ?? 0) > 0 && s.stock_status !== "out_of_stock"),
      )
    : undefined;

  // Normalize price fields
  const pricing = normalizePrice(raw);

  // Normalize images
  const imageData = normalizeImages(raw);

  // Normalize ratings
  const rating = normalizeRating(raw);

  // Build standardized Product object
  return {
    // Core fields
    id: raw.id,
    name: raw.name || raw.product_name || "",
    description: raw.description || "",
    sortType, // Inject sortType for UI badges

    // Pricing
    price: pricing.price,
    original_price: pricing.original_price,
    // sale_price: pricing.sale_price,
    sale_rate: pricing.sale_rate,
    discount: pricing.discount,

    // Images
    image: imageData.mainImage,
    images: imageData.allImages,
    image_paths: imageData.paths,

    // Relationships
    brand_id: raw.brand_id,
    brand_name: raw.brand_name || raw.brand?.name,
    brand: raw.brand,
    category_id: raw.category_id,
    category_name: raw.category_name || raw.category?.name,
    category: raw.category,

    // Status flags
    is_hidden: raw.is_hidden || false,
    is_featured: raw.is_featured || false,
    featured: raw.is_featured || false,
    is_hidden_category: raw.is_hidden_category || false,
    is_new:
      raw.is_new ||
      (raw.created_at
        ? new Date(raw.created_at) >
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        : false),
    on_sale: pricing.on_sale,
    trending:
      raw.trending ||
      (raw.popularity_score ? raw.popularity_score > 80 : false),
    // API provides in_stock directly for variant products — always trust it.
    // Fallback order: explicit raw.in_stock → derived from SKUs → qty>0 → true.
    in_stock:
      raw.in_stock !== undefined
        ? raw.in_stock
        : skuInStock !== undefined
          ? skuInStock
          : raw.is_unlimited_stock
            ? true
            : raw.quantity
              ? raw.quantity > 0
              : true,

    // Metadata
    popularity_score: raw.popularity_score || 0,
    rating,
    reviews: raw.review_count || raw.reviews || 0,
    review_count: raw.review_count || raw.reviews || 0,
    quantity: raw.quantity || 0,
    weight: raw.weight || 0,
    sales_count: raw.total_sales || raw.sales_count || 0,
    total_sales: raw.total_sales || raw.sales_count || 0,
    created_at: raw.created_at,
    updated_at: raw.updated_at,

    // Metadata
    store_id: raw.store_id,

    // Variant fields — pass through for ProductCard price range display
    has_variants: raw.has_variants || false,
    min_variant_price: derivedMinVariantPrice,
    max_variant_price: derivedMaxVariantPrice,
    default_variant_id: raw.default_variant_id,
    variant_options_summary: raw.variant_options_summary,
  };
}

// ============================================================================
// PRICE NORMALIZATION
// ============================================================================

/**
 * Normalize price fields with fallback strategy
 *
 * The database returns prices with inconsistent field names.
 * This function tries all possible variations and calculates derived values.
 *
 * Priority order for main price:
 * 1. usd_price (preferred)
 * 2. price
 * 3. current_price
 * 4. sale_price
 * 5. unit_price
 * 6. base_price
 * 7. selling_price
 */
interface PricingResult {
  price: number;
  original_price?: number;
  sale_price?: number | null;
  sale_rate?: number | null;
  discount?: number;
  on_sale: boolean;
}

function normalizePrice(raw: RawProductResponse): PricingResult {
  // Variant products: product-level usd_price/final_price are meaningless.
  // Prices live on each SKU; use min_variant_price as the listing display price.
  if (raw.has_variants) {
    const minPrice = raw.min_variant_price != null ? parseFloat(String(raw.min_variant_price)) : 0;
    return {
      price: minPrice,
      original_price: undefined,
      sale_price: null,
      sale_rate: null,
      discount: undefined,
      on_sale: false,
    };
  }

  // Try all possible price field names in priority order
  const priceFields = [
    "usd_price",
    "price",
    "current_price",
    "final_price",
    "sale_price",
    "unit_price",
    "base_price",
    "selling_price",
  ];

  let finalPrice = 0;
  for (const field of priceFields) {
    const value = raw[field];
    if (value !== null && value !== undefined && value !== 0) {
      finalPrice = parseFloat(String(value));
      break;
    }
  }

  // Determine if product is on sale
  const saleRate = raw.sale_rate || 0;
  const hasSalePrice = raw.sale_price !== null && raw.sale_price !== undefined;
  // If sale_rate > 0, it must be on sale regardless of the flag
  const isOnSale = (raw.is_on_sale ?? raw.on_sale ?? false) || saleRate > 0;

  // Get original price (already in database, no need to calculate)
  let originalPrice: number | undefined = undefined;
  if (raw.original_price) {
    originalPrice = parseFloat(String(raw.original_price));
  } else if (isOnSale && raw.usd_price) {
    // If on sale and no explicit original_price, use usd_price
    originalPrice = parseFloat(String(raw.usd_price));
  }

  // If get_client_products provided a final_price that differs from usd_price,
  // use final_price as the display price instead.
  if (raw.final_price !== undefined && raw.final_price !== null) {
    finalPrice = parseFloat(String(raw.final_price));
  }

  // Discount percentage (multiple possible sources)
  const discount =
    raw.sale_rate || raw.discount_percentage || raw.discount || 0;

  // Determine effective sale price
  let salePrice: number | null = null;
  if (hasSalePrice) {
    salePrice = parseFloat(String(raw.sale_price));
  } else if (isOnSale && raw.final_price) {
    salePrice = parseFloat(String(raw.final_price));
  }

  return {
    price: finalPrice,
    original_price: originalPrice,
    sale_price: salePrice,
    sale_rate: saleRate || null,
    discount: discount || undefined,
    on_sale: isOnSale,
  };
}

// ============================================================================
// IMAGE NORMALIZATION
// ============================================================================

/**
 * Normalize image paths and build consistent structure
 *
 * Images can come in multiple formats:
 * - Array of string paths: ['path1', 'path2']
 * - Array of objects: [{ path: 'path1' }, { path: 'path2' }]
 * - Mixed formats
 *
 * This function handles all cases and returns:
 * - mainImage: The primary product image URL
 * - allImages: Array of all image URLs
 * - paths: Raw paths for backward compatibility
 */
interface ImageResult {
  mainImage: string;
  allImages: string[];
  paths: string[];
}

function normalizeImages(raw: RawProductResponse): ImageResult {
  let imagePaths: string[] = [];

  // Case 1: image_paths is already an array of strings
  if (Array.isArray(raw.image_paths)) {
    imagePaths = raw.image_paths.filter(
      (path) => typeof path === "string" && path.length > 0,
    );
  }
  // Case 2: images is an array of objects with path property
  else if (Array.isArray(raw.images)) {
    imagePaths = raw.images
      .map((img: any) => {
        if (typeof img === "string") return img;
        if (typeof img?.path === "string") return img.path;
        return null;
      })
      .filter((path): path is string => path !== null && path.length > 0);
  }

  // Get the main image URL using existing utility
  const mainImage = getLastProductImageUrl(imagePaths);

  // Build all image URLs (if needed, can extend this logic)
  const allImages = imagePaths.length > 0 ? imagePaths : [mainImage];

  return {
    mainImage,
    allImages,
    paths: imagePaths,
  };
}

// ============================================================================
// RATING NORMALIZATION
// ============================================================================

/**
 * Normalize rating field
 * Rating can be string or number, and may be missing
 */
function normalizeRating(raw: RawProductResponse): number {
  // Prefer average_rating from get_client_products, fallback to rating
  const rawRating = raw.average_rating ?? raw.rating;

  if (rawRating === undefined || rawRating === null) {
    return 4.5; // Default rating if missing
  }

  const rating =
    typeof rawRating === "string" ? parseFloat(rawRating) : rawRating;

  // Ensure rating is between 0 and 5
  return Math.max(0, Math.min(5, rating));
}

// ============================================================================
// BATCH TRANSFORMATION
// ============================================================================

/**
 * Transform an array of raw products
 * More efficient than mapping individually for large datasets
 */
export function transformRawProducts(
  rawProducts: RawProductResponse[],
  options?: {
    sortType?: string;
    includeCalculations?: boolean;
  },
): Product[] {
  if (!Array.isArray(rawProducts)) {
    console.warn(
      "[transformRawProducts] Expected array, got:",
      typeof rawProducts,
    );
    return [];
  }

  return rawProducts.map((raw) => transformRawProduct(raw, options));
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if a product is on sale
 */
export function isProductOnSale(
  product: Product | RawProductResponse,
): boolean {
  return !!(product.on_sale || product.discount);
}

/**
 * Format price for display
 */
export function formatPrice(price: number, currency: string = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
}
